/**
 * PGN-Katalog: die Bedeutung der Parameter Group Numbers (J1939/Bus-FMS), die ein Fahrzeugbus
 * sendet. Schwesterdatei zu {@link ./telemetrie}: dort werden die fertig dekodierten Telemetrie-
 * felder beschriftet, hier die rohen PGNs aus dem Bus-Inventar (BUS_INVENTAR) des FMS-Geräts.
 *
 * Grundsatz wie bei telemetrie.ts: anzeigen, was der Bus wirklich sendet; nichts verschweigen.
 * Eine Bedeutung steht nur dort, wo sie belegt ist — die Zuordnungen stammen aus dem Geräte-
 * Dekoder (fms_pgn.py) und der FMS-0003-Busanalyse (vlzhub-fms,
 * docs/fahrzeugdaten/fms-0003-busdaten-2026-10-20.md). Was wir nicht sicher kennen, bleibt
 * „nicht zugeordnet" und wird roh mit seiner PGN-Nummer gezeigt, nicht erfunden.
 *
 * `kategorie` ordnet grob ein, damit die Ansicht gruppieren/einfärben kann:
 *   kern         — vom Gerät dekodierte Standard-FMS-Werte (fahrbarer Kern)
 *   zustand      — Fahrzeugzustand, aus der Analyse zugeordnet (noch nicht alle dekodiert)
 *   transport    — Transport-/Diagnoseprotokoll (Mehrpaket, UDS), kein Einzelwert
 *   proprietaer  — herstellerspezifisch, meist ohne lesbaren Wert (durchgängig FF)
 */

export type PgnKategorie = 'kern' | 'zustand' | 'transport' | 'proprietaer'

export interface PgnInfo {
  label: string
  hinweis?: string
  kategorie: PgnKategorie
}

export const KATEGORIE_LABEL: Record<PgnKategorie, string> = {
  kern: 'Kern (dekodiert)',
  zustand: 'Fahrzeugzustand',
  transport: 'Transport/Diagnose',
  proprietaer: 'herstellerproprietär',
}

/** Bekannte PGNs mit Klartext-Bedeutung. Schlüssel ist die dezimale PGN-Nummer. */
export const PGN: Record<number, PgnInfo> = {
  // ---- Kern: vom Gerät dekodierte Standard-FMS-Werte ----
  61443: { label: 'EEC2 — Fahrpedal, Motorlast', kategorie: 'kern' },
  61444: { label: 'EEC1 — Motordrehzahl', hinweis: 'Verbrennungsmotor; bei E-Bus nicht gesendet', kategorie: 'kern' },
  64695: {
    label: 'HV-Batterie — Ladezustand (SoC)',
    hinweis: 'herstellerspez., SA 252; Byte 0–1 little-endian, SoC % ≈ roh/395 (OMNIplus-kalibriert)',
    kategorie: 'kern',
  },
  64777: { label: 'HRLFC — Kraftstoff gesamt (hochauflösend)', kategorie: 'kern' },
  64932: { label: 'PTODE — Nebenantrieb', kategorie: 'kern' },
  64977: { label: 'FMS — Gateway-Version/Fähigkeiten', kategorie: 'kern' },
  65131: { label: 'DI — Fahrer-Identifikation', kategorie: 'kern' },
  65132: { label: 'TCO1 — Tachograph (Fahrerzustände, Tacho-Tempo)', kategorie: 'kern' },
  65216: { label: 'SERV — Reststrecke bis Wartung', kategorie: 'kern' },
  65217: { label: 'VDHR — Wegstrecke gesamt (Kilometerstand)', kategorie: 'kern' },
  65253: { label: 'HOURS — Betriebsstunden', kategorie: 'kern' },
  65257: { label: 'LFC — Kraftstoff gesamt', kategorie: 'kern' },
  65258: { label: 'VW — Achslast', kategorie: 'kern' },
  65260: { label: 'VI — Fahrzeugidentifikation (VIN)', kategorie: 'kern' },
  65262: { label: 'ET1 — Kühlmitteltemperatur', kategorie: 'kern' },
  65265: { label: 'CCVS — Geschwindigkeit, Bremse, Tempomat', kategorie: 'kern' },
  65266: { label: 'LFE — Momentanverbrauch', kategorie: 'kern' },
  65269: { label: 'AMB — Außen- und Innenraumtemperatur', hinweis: 'trägt beide Temperaturen', kategorie: 'kern' },
  65276: { label: 'DD — Tankfüllstand/Dash', hinweis: 'beim E-Bus FF = kein Wert (kein Kraftstofftank)', kategorie: 'kern' },

  // ---- Fahrzeugzustand: aus der FMS-0003-Analyse zugeordnet ----
  61441: { label: 'EBC1 — Bremse (Pedalstellung, Schalter, ABS)', kategorie: 'zustand' },
  61445: { label: 'ETC2 — Getriebe (gewählter/aktueller Gang)', kategorie: 'zustand' },
  61449: { label: 'VDC2 — Fahrdynamik (Lenkwinkel, Gierrate)', kategorie: 'zustand' },
  64893: { label: 'Tell-Tale — Kontrollleuchten/Störungsanzeige', hinweis: 'FMS-Tell-Tale, Kandidat fürs Dashboard', kategorie: 'zustand' },
  64933: { label: 'DC1 — Türsteuerung 1 (Türstatus)', kategorie: 'zustand' },
  65102: { label: 'DC2 — Türsteuerung 2 (einzelne Türen)', kategorie: 'zustand' },
  65112: { label: 'Luftfederung/Achslast (vermutet)', hinweis: 'SA 47, 4 veränderliche Kanäle', kategorie: 'zustand' },
  65198: { label: 'AIR1 — Druckluftvorrat (Bremsanlage)', kategorie: 'zustand' },
  65254: { label: 'TD — Uhrzeit/Datum', kategorie: 'zustand' },

  // ---- Transport-/Diagnoseprotokoll (Mehrpaket) ----
  55808: { label: 'UDS-Diagnose (ISO-TP)', kategorie: 'transport' },
  60160: { label: 'TP.DT — Transportprotokoll Datenpaket', kategorie: 'transport' },
  60416: { label: 'TP.CM — Transportprotokoll Verbindung', kategorie: 'transport' },

  // ---- Herstellerproprietär / meist ohne Wert (durchgängig FF) ----
  64962: { label: 'herstellerproprietär (FDC2)', hinweis: 'ohne lesbaren Wert', kategorie: 'proprietaer' },
  65110: { label: 'AdBlue (FE56)', hinweis: 'durchgängig FF = kein Wert', kategorie: 'proprietaer' },
  65199: { label: 'herstellerproprietär (FEAF)', hinweis: 'ohne lesbaren Wert', kategorie: 'proprietaer' },
  65237: { label: 'herstellerproprietär (FED5)', hinweis: 'ohne lesbaren Wert', kategorie: 'proprietaer' },
  65299: { label: 'herstellerproprietär (FF13)', hinweis: 'SA 23', kategorie: 'proprietaer' },
  65408: { label: 'herstellerproprietär (FF80)', hinweis: 'SA 37, durchgängig FF', kategorie: 'proprietaer' },
  65502: { label: 'herstellerproprietär (FFDE)', hinweis: 'SA 238', kategorie: 'proprietaer' },
}

/** PGN als Hex-Kürzel, z. B. 65265 → „FEF1". */
export function pgnHex(pgn: number): string {
  return pgn.toString(16).toUpperCase().padStart(4, '0')
}

/**
 * Bedeutung einer PGN — erst der Katalog, dann bereichsweise Sonderfälle (der proprietäre
 * Block 65329–65348 / FF31–FF44 von SA 74, über Tage durchgängig FF). `undefined` heißt
 * „nicht zugeordnet"; die Ansicht zeigt die PGN dann roh.
 */
export function pgnInfo(pgn: number): PgnInfo | undefined {
  const treffer = PGN[pgn]
  if (treffer) return treffer
  if (pgn >= 65329 && pgn <= 65348) {
    return {
      label: `proprietärer Block (FF${(pgn - 65329 + 0x31).toString(16).toUpperCase()})`,
      hinweis: 'SA 74, 20er-Block eines Steuergeräts — über Tage durchgängig FF, auch beim Laden',
      kategorie: 'proprietaer',
    }
  }
  return undefined
}
